import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons/faCircleInfo";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, CardContent, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import { AppImageDimension, AppImages } from "app/appImages";
import { MprcCard, MprcIconButton } from "components";
import { AutoCompleteDataModel } from "components/MprcAutoComplete";
import { MprcContainer } from "components/MprcContainer";
import { MprcImageSelection } from "components/MprcImageHandler";
import { MprcInformationDialog } from "components/MprcInformationDialog";
import { MprcStringToHtml } from "components/MprcStringToHtml";
import { UserEditProfileResponse, UserEditProfileSettings } from "generated/CoreApi_v1";
import MediaUtils from "lib/MediaUtils";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomFieldInput, getCustomFieldGridSize } from "views/foundation/customField/components/CustomFieldInput";
import { WorkplaceAutoComplete } from "views/foundation/workplace/components/WorkplaceAutoComplete";

////////////////////////////////////////////////////////////////////////////

interface IValidationFields {
    displayName: boolean;
    name: boolean;
    preferredLocale: boolean;
    customFields: { [key: string]: boolean; };
    email: boolean;
    thumbnail?: boolean;
    workplace?: boolean;
    workplaceText?: boolean;
}

interface IPostValidation {
    name: string[] | undefined;
    displayName: string[] | undefined;
    email: string[] | undefined;
    workplace: string[] | undefined;
    workplaceText: string[] | undefined;
    thumbnail: string[] | undefined;
    preferredLocale: string[] | undefined;
    customFields: { [key: string]: string[] | undefined; };
}

interface IProps {
    viewData: UserEditProfileResponse;
    viewErrors: ApiProblemErrors | undefined;
    onFormUpdate: (dto: UserEditProfileResponse) => void;
    onFormValidation: (valid: boolean) => void;
    onError: (e: any) => void;
}

////////////////////////////////////////////////////////////////////////////

function getDefaultValidate(
    currentValidation: IValidationFields,
    dto: UserEditProfileResponse,
    settings: UserEditProfileSettings,
    ignoreCustomFields: boolean
): IValidationFields {
    var obj = {
        ...currentValidation,
        name: dto.name != undefined && dto.name.length > 0,
        email: (dto.email != undefined && dto.email.length > 0) || !settings.isEmailRequired,
        workplace: !settings.workplaceRequiredForNewUsers || settings.isWorkplaceFreeText || (dto.workplace?.workplaceId != undefined),
        workplaceText: !settings.workplaceRequiredForNewUsers || !settings.isWorkplaceFreeText || (dto.workplaceText != undefined && dto.workplaceText.length > 0),
        displayName: dto.displayName != undefined && dto.displayName.length > 0,
        preferredLocale: dto.preferredLocale != undefined && dto.preferredLocale.length > 0,
    } as IValidationFields;

    if (!ignoreCustomFields) {
        dto.customFieldsBasic
            ?.filter((x) => x.isRequired)
            .forEach((f) => (obj.customFields[f.fieldId] = f.fieldValue != undefined && f.fieldValue.length > 0));
    }

    return obj;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Edição de Usuário - Formulário principal
 * @param props
 */
export const EditUserMainBox: React.FC<IProps> = (props) => {
    const { viewData, viewErrors, onFormUpdate, onFormValidation, onError } = props;

    const session = useSession();
    const { t } = useTranslation();

    const settings = viewData.settings;
    const permissions = viewData.permissions;

    const isSelfUser = viewData.userId == session.user.userId;
    const canUpdatePersonal = permissions.allowUpdatePersonal;
    const canUpdateName = permissions.allowUpdateName;
    const canUpdateLocal = permissions.allowUpdateLocal;

    const [dialog, setDialog] = useState<any>(undefined);
    const [formValidation, setFormValidation] = useState<IValidationFields>(
        getDefaultValidate({ customFields: {} } as IValidationFields, viewData, settings, false)
    );

    const vErrors = viewErrors ?? {};
    const postValidation: IPostValidation = {
        name: vErrors["name"],
        displayName: vErrors["displayName"],
        email: vErrors["email"],
        workplace: vErrors["workplaceId"],
        workplaceText: vErrors["workplaceText"],
        thumbnail: vErrors["thumbnailId"],
        preferredLocale: vErrors["preferredLocale"],
        customFields: {}
    };
    viewData.customFieldsBasic?.map((f) => (postValidation.customFields[f.fieldId] = vErrors[`customFields.${f.fieldId}`] ?? undefined));

    ////////////////////////////////////////////////////////////////////////////
    ///
    function handleUpdateRecord(name: keyof UserEditProfileResponse, v: any) {
        setFormValidation((state) =>
            getDefaultValidate(state, { ...viewData, [name]: v }, settings, true)
        );

        onFormUpdate({ ...viewData, [name]: v });
    }

    ///
    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | SelectChangeEvent) => {
        const inputName = event.target.name as keyof UserEditProfileResponse;
        handleUpdateRecord(inputName, event.target.value);
    };

    ///
    const handleUpdateCustomField = (fieldId: string, value: any) => {
        let newCustomFields = viewData.customFieldsBasic?.map((item) => {
            if (item.fieldId === fieldId) item.fieldValue = value;
            return item;
        });

        handleUpdateRecord("customFieldsBasic", newCustomFields);
    };

    ///
    const setSamlInfoDialog = () => {
        setDialog(
            <MprcInformationDialog
                message={
                    <>
                        <p>{t("User.Edit.SamlAccountInfoMessageL1")}</p>
                        <ul>
                            <li>{t("User.Edit.SamlAccountInfoMessageL2")}</li>
                            <li>{t("User.Edit.SamlAccountInfoMessageL3")}</li>
                        </ul>
                    </>
                }
                onClose={() => setDialog(undefined)}
            />
        );
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        onFormValidation(
            Object.values(formValidation).filter((x) => x === false).length == 0 &&
            Object.entries(formValidation.customFields).filter(([key, v]) => v === false).length == 0
        );
    }, [formValidation]);

    return (
        <div>
            {dialog}
            <MprcContainer>
                <MprcCard>
                    <CardContent>
                        {viewData.hasSamlAccount && (
                            <MprcContainer>
                                <Alert
                                    icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                                    severity="warning"
                                    action={
                                        <MprcIconButton color="cancel" size="small" onClick={setSamlInfoDialog}>
                                            <FontAwesomeIcon icon={faCircleInfo} />
                                        </MprcIconButton>
                                    }
                                >
                                    {t("User.Edit.UserHasSamlAccountMessage")}
                                </Alert>
                            </MprcContainer>
                        )}
                        <Grid container spacing={2}>
                            <Grid alignSelf="flex-start" justifySelf="center">
                                <MprcImageSelection
                                    variant="circular"
                                    allowEdit
                                    allowSelect={permissions.allowUpdatePhoto}
                                    enableClear={permissions.allowUpdatePhoto}
                                    initialSource="local"
                                    backgroundType="solid"
                                    emptyImageUrl={AppImages.UserThumbnail}
                                    currentImageUrl={viewData.thumbnail?.url}
                                    acceptedFiles={MediaUtils.DropzoneAcceptImages()}
                                    imageDimensions={AppImageDimension.SquarePicture}
                                    onClear={() => {
                                        handleUpdateRecord("thumbnail", null);
                                    }}
                                    onFileUploadSuccess={(data) => {
                                        handleUpdateRecord("thumbnail", data.thumbnail ? data.thumbnail : data.mainFile);
                                        return true;
                                    }}
                                    onFileUploadStarted={() => setFormValidation({ ...formValidation, thumbnail: false })}
                                    onFileUploadFinished={() => setFormValidation({ ...formValidation, thumbnail: true })}
                                />
                                <FormHelperText error={postValidation?.thumbnail != undefined}>
                                    <MprcStringToHtml text={postValidation?.thumbnail} color="error" />
                                </FormHelperText>
                            </Grid>
                            <Grid size="grow">
                                <MprcContainer>
                                    <TextField
                                        fullWidth
                                        required
                                        name="name"
                                        label={t("User.Edit.FullNameLabel")}
                                        disabled={!canUpdateName && !canUpdatePersonal}
                                        error={(viewData.name != undefined && !formValidation.name) || postValidation?.name != undefined}
                                        helperText={postValidation?.name && <MprcStringToHtml text={postValidation?.name} color="error" />}
                                        margin="dense"
                                        value={viewData.name || ""}
                                        onChange={(e) => handleChange(e)}
                                        slotProps={{
                                            input: {
                                                inputProps: { maxLength: 255 }
                                            }
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        required
                                        name="displayName"
                                        label={t("User.Edit.DisplayNameLabel")}
                                        disabled={!canUpdateName && !canUpdatePersonal}
                                        error={
                                            (viewData.displayName != undefined && !formValidation.displayName) ||
                                            postValidation?.displayName != undefined
                                        }
                                        helperText={
                                            postValidation?.displayName && (
                                                <MprcStringToHtml text={postValidation?.displayName} color="error" />
                                            )
                                        }
                                        margin="dense"
                                        value={viewData.displayName || ""}
                                        onChange={(e) => handleChange(e)}
                                        slotProps={{
                                            input: {
                                                inputProps: { maxLength: 255 }
                                            }
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        required={settings.isEmailRequired}
                                        name="email"
                                        label={t("User.Edit.EmailLabel")}
                                        disabled={viewData.hasSamlAccount || !canUpdatePersonal}
                                        error={(viewData.email != undefined && !formValidation.email) || postValidation?.email != undefined}
                                        helperText={
                                            postValidation?.email && <MprcStringToHtml text={postValidation?.email} color="error" />
                                        }
                                        margin="dense"
                                        value={viewData.email || ""}
                                        onChange={(e) => handleChange(e)}
                                        slotProps={{
                                            input: {
                                                inputProps: { maxLength: 2048 }
                                            }
                                        }}
                                    />
                                    <Grid container columnSpacing={4} alignItems={"end"}>
                                        {viewData.customFieldsBasic?.filter(p => !isSelfUser || p.isVisibleToSelfUser === true).map((item, i) => (
                                            <Grid key={i} size={{ xs: getCustomFieldGridSize(item.showWidth) }}>
                                                <CustomFieldInput
                                                    field={item}
                                                    onChange={(value, formattedValue, isValid) => {
                                                        handleUpdateCustomField(item.fieldId, value);
                                                        item.isRequired &&
                                                            setFormValidation({
                                                                ...formValidation,
                                                                customFields: { ...formValidation.customFields, [item.fieldId]: isValid }
                                                            });
                                                    }}
                                                    error={postValidation.customFields[item.fieldId]}
                                                    validateOnFirstMount={true}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>

                                    <Grid container spacing={4} alignItems="flex-end">
                                        <Grid size={{ xs: 6 }}>
                                            <FormControl
                                                required
                                                fullWidth
                                                error={viewData.preferredLocale != undefined && !formValidation.preferredLocale}
                                                sx={{ mb: "4px", mt: "8px" }}
                                            >
                                                <InputLabel>{t("User.Edit.LocaleLabel")}</InputLabel>
                                                <Select
                                                    error={postValidation?.preferredLocale != undefined}
                                                    name="preferredLocale"
                                                    value={viewData.preferredLocale || ""}
                                                    label={t("User.Edit.LocaleLabel")}
                                                    disabled={!canUpdatePersonal && !isSelfUser}
                                                    onChange={(e) => handleChange(e)}
                                                >
                                                    {session.config.inputLocales.map((l) => (
                                                        <MenuItem key={l.locale} value={l.locale}>
                                                            {l.nameR.find(x => x.locale == session.uiCulture)?.text ?? l.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                <FormHelperText error={postValidation?.preferredLocale != undefined}>
                                                    <MprcStringToHtml text={postValidation?.preferredLocale} color="error" />
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                    {settings.isWorkplaceFreeText === true && (
                                        <TextField
                                            fullWidth
                                            name="workplaceText"
                                            required={settings.workplaceRequiredForNewUsers}
                                            label={settings.workplaceLabel}
                                            disabled={!canUpdateLocal}
                                            margin="dense"
                                            value={viewData.workplaceText || ""}
                                            onChange={(e) => handleChange(e)}
                                            slotProps={{
                                                input: {
                                                    inputProps: { maxLength: 255 }
                                                }
                                            }}
                                            error={(viewData.workplaceText != undefined && !formValidation.workplaceText) || postValidation?.workplaceText != undefined}
                                            helperText={<MprcStringToHtml text={postValidation?.workplaceText} color="error" />}
                                        />
                                    )}
                                    {settings.isWorkplaceFreeText === false &&
                                        <WorkplaceAutoComplete
                                            label={settings.workplaceLabel}
                                            required={settings.workplaceRequiredForNewUsers}
                                            multiSelect={false}
                                            disabled={!canUpdateLocal}
                                            onError={onError}
                                            selectedItems={
                                                viewData.workplace != undefined ?
                                                    [{ key: viewData.workplace.workplaceId, label: viewData.workplace.name, canDelete: true } as AutoCompleteDataModel] :
                                                    []
                                            }
                                            onChange={(items) => {
                                                if (items.length > 0) handleUpdateRecord("workplace", { workplaceId: items[0].key, name: items[0].label });
                                                else handleUpdateRecord("workplace", null);
                                            }}
                                            error={(viewData.workplace !== undefined && !formValidation.workplace) || postValidation?.workplace != undefined}
                                            helperText={<MprcStringToHtml text={postValidation?.workplace} color="error" />}
                                        />
                                    }
                                </MprcContainer>
                            </Grid>
                        </Grid>
                    </CardContent>
                </MprcCard>
            </MprcContainer>
        </div>
    );
};
